import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  FaExclamationCircle,
  FaClock,
  FaCreditCard,
  FaInfoCircle,
} from 'react-icons/fa';
import currencyFormatter from 'currency-formatter';
import moment from 'moment';
import {
  groupBillingItems,
  groupBillingItemsChangeFleetBillingPlan,
} from '../billingUsage/utils/billingCommonFunc';
import './subscriptionAtRisk.scss';
import { callApiWithoutDispatch } from '../../utils/apiUtils';
import {
  onBoardingGetFleetBillingPendingInvoiceApi,
  onBoardingGetFleetBillingUrlApi,
} from '../../constants/ApiConfigs';
import PropTypes from 'prop-types';
import { BILLING_PLAN_TYPE } from '../../features/signup/constantStep';

const SubscriptionAtRisk = ({
  auth,
  billingUsage,
  loadingActions,
  topOffset = 0,
}) => {
  // Get data from first invoice and first payment method
  const [invoice, setInvoice] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [fleetBillingUrl, setFleetBillingUrl] = useState(null);

  useEffect(() => {
    handleLoadInitialData();
  }, []);

  const handleLoadInitialData = async () => {
    try {
      loadingActions.showLoadingSpiner();
      const response = await callApiWithoutDispatch(
        onBoardingGetFleetBillingPendingInvoiceApi,
        {
          method: 'POST',
          body: JSON.stringify({
            fleetId: auth?.selectedFleet?.fleetId,
          }),
        }
      );
      if (response?.res?.data) {
        setInvoice(response.res.data?.invoices[0]);
        setPaymentMethod(response.res.data?.paymentMethods[0]);
      }
      console.log(response);
    } catch (error) {
      console.log(error);
    } finally {
      loadingActions.hideLoadingSpiner();
    }
  };

  const handleGetFleetBillingUrl = async () => {
    loadingActions.showLoadingSpiner();
    try {
      const response = await callApiWithoutDispatch(
        onBoardingGetFleetBillingUrlApi,
        {
          method: 'GET',
          params: {
            fleetId: auth?.selectedFleet?.fleetId,
            returnUrl: window.location.href,
          },
        }
      );
      if (response?.res?.url) {
        window.open(response?.res?.url, '_blank');
      }
    } catch (error) {
      console.log(error);
    } finally {
      loadingActions.hideLoadingSpiner();
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return currencyFormatter.format(amount, {
      code: currency?.toUpperCase() || currency,
    });
  };

  const getCardIconClass = (cardType) => {
    return cardType?.toLowerCase() || 'visa';
  };

  // Check if card is expired
  const isCardExpired = useMemo(() => {
    if (!paymentMethod?.expMonth || !paymentMethod?.expireYear) return false;
    const now = moment();
    const expiryDate = moment(
      `${paymentMethod.expireYear}-${String(paymentMethod.expMonth).padStart(
        2,
        '0'
      )}`,
      'YYYY-MM'
    );
    return expiryDate.isBefore(now, 'month');
  }, [paymentMethod]);

  // Format billing period dates
  const billingPeriod = useMemo(() => {
    if (!invoice?.periodStart || !invoice?.periodEnd)
      return { start: '', end: '' };
    return {
      start: moment.unix(invoice.periodStart).format('MMM D'),
      end: moment.unix(invoice.periodEnd).format('MMM D, YYYY'),
    };
  }, [invoice]);

  // Format cancellation date from dueDate
  const cancellationDate = useMemo(() => {
    if (!invoice?.dueDate) return '';
    return moment.unix(invoice.dueDate).format('MMMM D, YYYY');
  }, [invoice]);

  // Calculate days/hours left from dueDate
  const timeLeft = useMemo(() => {
    if (!invoice?.dueDate) return { text: '', isHours: false };
    const now = moment();
    const dueDate = moment.unix(invoice.dueDate);
    const diffInHours = dueDate.diff(now, 'hours', true);

    if (diffInHours <= 0) {
      return { text: 'EXPIRED', isHours: false };
    } else if (diffInHours > 24) {
      const days = Math.floor(diffInHours / 24);
      return { text: `${days}`, isHours: false };
    } else {
      const hours = Math.floor(diffInHours);
      const minutes = Math.max(0, Math.floor((diffInHours - hours) * 60));
      return { text: `${hours}h ${minutes}m`, isHours: true };
    }
  }, [invoice]);

  // Extract plan and add-ons from invoice items using groupBillingItems
  if (!invoice) {
    return (
      <div className="subscription-at-risk-container">
      </div>
    );
  }
  console.log(invoice.items);
  const { plan, addOns, tax } = groupBillingItemsChangeFleetBillingPlan(
    invoice.items || []
  );
  return (
		<div style={{height: '100%', overflow: 'auto'}}>
			<div style={{width: '100%', backgroundColor: '#F04438', position: 'fixed', top: topOffset || 0, left: '0', minHeight: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 20px', zIndex: 1040}}>
				<span style={{color: '#FFFFFF', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center'}}>
					<FaExclamationCircle className="alert-icon" style={{marginRight: '10px'}} /> 
					{`We couldn’t process your payment. You have ${timeLeft.text} ${timeLeft.isHours ? 'hours' : 'days'} to update your payment method before service interruption.`}
				</span>
				<Button
					className="btn-update-payment"
					href={invoice.invoiceUrl}
					target="_blank"
					variant="link"
					style={{color: '#FFFFFF', fontSize: '14px', fontWeight: 700, border: '1px solid #FFFFFF', padding: '5px 15px', borderRadius: '5px'}}
				>
					Make Payment
				</Button>
			</div>
			<div className="subscription-at-risk-container">
				{/* Payment Failed Alert Banner */}
				<div className="subscription-at-risk-content">
					<div className="payment-failed-alert">
						<FaExclamationCircle className="alert-icon" color="#EF4444" />
						<div className="alert-content">
							<strong style={{ color: '#FFC9C9' }}>Payment Failed.</strong> <br />{' '}
							Your subscription payment could not be processed. Please update your
							payment method to continue using the service.
						</div>
					</div>

					{/* Subscription at Risk Section */}
					<div className="subscription-at-risk-section">
						<div className="section-header">
							<div className="header-content">
								<h2>Subscription at Risk</h2>
								<p className="subtitle">
									Action required to avoid service interruption
								</p>
							</div>
							<div
								className={`days-left-badge ${
									timeLeft.isHours ? 'hours-left' : ''
								}`}
							>
								<span>{timeLeft.text}</span>
								<span>{timeLeft.isHours ? 'LEFT' : 'DAYS LEFT'}</span>
							</div>
						</div>
						<div className="warning-message">
							<FaClock className="warning-icon" color="#6B7280" />
							<p>
								Your subscription will be{' '}
								<strong>cancelled on {cancellationDate}</strong> if payment is not
								received. After cancellation, you will lose access to all premium
								features and your account data may be archived.
							</p>
						</div>
					</div>
					{
						paymentMethod && (
							<>
							</>
						)
					}
					<div className="payment-details-section">
						<h3>Payment Details</h3>
						<div className="payment-details-content">
							{/* Payment Method */}
							{
								paymentMethod && (
									<>
										<div className="detail-row">
											<div className="detail-label">
												<div className="payment-method-info">
													<div className="card-icon">
														<i
															id="creditcard"
															className={getCardIconClass(paymentMethod.cardType)}
														/>
													</div>
													<div
														style={{
															display: 'flex',
															flexDirection: 'column',
															alignItems: 'flex-start',
														}}
													>
														<span>Payment Method</span>
														<span className="card-number">
															**** {paymentMethod.last4}
														</span>
													</div>
												</div>
											</div>
										</div>
									</>
								)
							}

							{/* Plan */}
							{plan && plan.length > 0 && (
								<div className="detail-row">
									<div
										className="planDiv"
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-start',
										}}
									>
										<span
											className="plan-label"
											style={{
												color: 'white',
												fontSize: '16px',
												fontWeight: 600,
											}}
										>
											Plan
										</span>
										<span
											style={{
												fontSize: '16px',
												fontWeight: 400,
												color: '#E2E8F0',
											}}
										>
											{plan[0].name}
										</span>
									</div>
									<div className="detail-value">
										<span
											className="amount"
											style={{
												color: '#E2E8F0',
												fontSize: '16px',
												fontWeight: 400,
											}}
										>
											{formatCurrency(plan[0].amount, invoice.currencyISO)}/
											{billingUsage.billingType === BILLING_PLAN_TYPE.annual
												? 'Annual'
												: 'Monthly'}
										</span>
									</div>
								</div>
							)}

							{/* Add-Ons */}
							{addOns && addOns.length > 0 && (
								<div style={{ borderBottom: '1px solid rgba(71, 84, 103, 0.5)', paddingBottom: '5px' }}>
									<p style={{ color: 'white', fontSize: '16px', fontWeight: 700 , paddingTop: '10px', marginBottom: '10'}}>Add-Ons</p>
									{addOns?.map((item) => {
										return (
											<div>
												<p key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
													<span style={{ color: '#94A3B8', fontSize: '16px', fontWeight: 400 }}>{item.name}</span>
													<span
														className={`add-on-price ${
															item.amount <= 0 ? 'negative' : ''
														}`}
														style={{ color: '#E2E8F0', fontSize: '16px', fontWeight: 400 }}
													>
														{`${currencyFormatter.format(item.amount || 0, {
															code: billingUsage.currencyISO,
														})}/${
															billingUsage?.billingType ===
															BILLING_PLAN_TYPE.annual
																? 'Annual'
																: 'Monthly'
														}`}
													</span>
												</p>
												{item.items.map((item) => {
													return (
														<p key={item._id} style={{ marginLeft: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
															<span style={{ color: '#94A3B8', fontSize: '16px', fontWeight: 400 }}>{item.name}</span>
															<span style={{ color: '#E2E8F0', fontSize: '16px', fontWeight: 400 }}>
																{`${currencyFormatter.format(item.amount || 0, { code: billingUsage.currencyISO })}/${billingUsage?.billingType === BILLING_PLAN_TYPE.annual ? 'Annual' : 'Monthly'}`}
															</span>
														</p>
													);
												})}
											</div>
										);
									})}
								</div>
							)}

							{/* Tax */}
							{
								tax?.length > 0 && (
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
										<p style={{ fontSize: '16px', fontWeight: '700', marginTop: '10px' }}>Tax</p>
										<p>{currencyFormatter.format(tax?.[0]?.amount || 0, { code: billingUsage.currencyISO })}</p>
									</div>
								)
							}

							{/* Billing Period */}
							<div className="detail-row">
								<div className="detail-label">Billing Period</div>
								<div className="detail-value">
									{billingPeriod.start} - {billingPeriod.end}
								</div>
							</div>

							{/* Amount Due */}
							<div className="detail-row amount-due-row">
								<div className="detail-label">Amount Due</div>
								<div className="detail-value amount-due">
									<strong>
										{formatCurrency(invoice.amountDue, invoice.currencyISO)}
									</strong>
								</div>
							</div>
						</div>
					</div>
					{/* Payment Details Section */}

					{/* Action Buttons */}
					<div className="action-buttons-section">
						<Button
							className="btn-pay-balance"
							href={invoice.invoiceUrl}
							target="_blank"
						>
							<FaCreditCard className="btn-icon" />
							Pay Balance Due
						</Button>
						<Button
							className="btn-update-payment"
							onClick={handleGetFleetBillingUrl}
						>
							Update Payment Method
						</Button>
					</div>

					{/* Help Section */}
					<div className="help-section">
						<FaInfoCircle className="help-icon" />
						<p>
							Need help?{' '}
							<a
								href={`mailto:${process.env.REACT_APP_EMAIL_SUPPORT}`}
								className="help-link"
							>
								Contact support.
							</a>
						</p>
					</div>
				</div>
			</div>
		</div>
  );
};

export default SubscriptionAtRisk;
